import { GridStack } from "gridstack";
import {
  useState,
  useEffect,
  createRef,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import styles from "./Dashboard.module.css";
import {
  drawWidgets,
  ItemType,
  ItemTypeProperties,
} from "../widgets/WidgetTypes";
import { useDispatch, useSelector } from "react-redux";

import "gridstack/dist/gridstack.css";
import "gridstack/dist/gridstack.min.css";
import "gridstack/dist/gridstack-extra.min.css";
import { setlastClickedItemId } from "src/store/dashboardSlice";
import NetworkEffect from "src/pachinko/NetworkEffect";
import { Grip } from "lucide-react";

export const Item = ({
  item,
  onRemove,
  dashboardMode,
  highlighted,
  sidelist = false,
  containerRef,
}) => {
  return (
    <div
      className="relative group w-full h-full "
      // className="relative group w-full h-full p-[2px] rounded-xl bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-[length:200%] animate-gradient-x shadow-[0_0_80px_rgba(168,85,247,0.6)]"
      style={{ overflow: "visible" }}
    >
      {/* Floating Header (visible on hover of group) */}
      {(dashboardMode === "EDIT" ||
        item.type === ItemType.BADGES ||
        item.type === ItemType.THREED) && (
        <div
          className="z-30 card-header absolute -top-11 left-0 right-0  bg-gradient-to-r from-blue-500 to-purple-500 text-white text-lg font-semibold py-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-90 transition-all duration-300 pointer-events-auto
        rounded-t-lg border-t-primary border-2 border-l-primary border-r-primary border-b-transparent"
        >
          <div className="flex justify-end">
            {dashboardMode === "EDIT" && (
              <button
                className="btn btn-square btn-outline dashboard-remove-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove({ e, item });
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 12 12"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 9L9 3M3 3l6 6"
                  />
                </svg>
              </button>
            )}
            <div className="w-10">
              <Grip />
            </div>
          </div>
        </div>
      )}
      <div
        className={`z-20 card bg-gray-800 bg-opacity-50 h-full border-2 transition-colors duration-300 ${
          highlighted ? "border-blue-500" : "border-transparent"
        } overflow-hidden`}
      >
        {drawWidgets(item, sidelist, containerRef)}
      </div>
    </div>
  );
};

//
// Controlled example
//
const Dashboard = forwardRef(
  (
    {
      items,
      addItem,
      getDashboardSettings = () => {},
      removeItem,
      onItemsChanged,
      dashboardMode,
    },
    ref
  ) => {
    const refs = useRef({});
    const itemContainerRefs = useRef({});
    const gridRef = useRef();
    const dispatch = useDispatch();
    const gridContainerRef = useRef();
    const isPhoneRef = useRef(null);
    const [isPhone, setIsPhone] =
      useState(null);

    const [currentDashboardMode, setCurrentDashboardMode] =
      useState(dashboardMode);

    items.forEach(({ id }) => {
      refs.current[id] = refs.current[id] || createRef();
      itemContainerRefs.current[id] =
        itemContainerRefs.current[id] || createRef();
    });

    //Use this forwarded ref in the edit screen to get the current items x,y,w,h
    useImperativeHandle(ref, () => ({
      saveLayout: () => {
        return gridRef.current.save();
      },
    }));

    const handleResize = () => {
          const width = window.innerWidth;
          const grid = gridRef.current;
          if (!grid) return;
          if (width < 640) {
            if (isPhoneRef.current === false || isPhoneRef.current === null) {
              isPhoneRef.current = true;
              setIsPhone(true);

              grid.column(2); // Mobile
              gridRef.current.batchUpdate();

              gridRef.current.removeAll(true); // clear grid
              //hardcoded mobile layout.
              let hardcodedMobileLayout = {
  badges: {x: 0, y: 6, w:2, h:1},
  text:{x: 0, y: 0, w:2, h:1},
  datavis:{x: 0, y: 1, w:2, h:2},
  calendar:{x: 0, y: 3, w:2, h:1},
  threed:{x: 0, y: 7, w:1, h:1},
  chat:{x: 0, y: 4, w:2, h:2}
              };
              
              items.forEach(({ id, type }, idx) => {
                
                const el = refs.current[id]?.current;
                if (!el) return;
                if(type === ItemType.CONTEXT){
                  grid.removeWidget(el, false);
                  return;}
                el.setAttribute("gs-w", hardcodedMobileLayout[type].w);
                el.setAttribute("gs-h", hardcodedMobileLayout[type].h);
                el.setAttribute("gs-x", hardcodedMobileLayout[type].x);
                el.setAttribute("gs-y", hardcodedMobileLayout[type].y);
                el.setAttribute("gs-no-move", true);
                gridRef.current.makeWidget(el);
              });

              gridRef.current.batchUpdate(false);
            }
          } else {
            if (isPhoneRef.current === true || isPhoneRef.current === null) {
              isPhoneRef.current = false;
              setIsPhone(false);
              grid.column(12); //gridstack is default 12 and cannot go higher.
                            gridRef.current.batchUpdate();

              gridRef.current.removeAll(true); // clear grid
              items.forEach(({ id, type,x,y,w,h }, idx) => {
                const el = refs.current[id]?.current;
                if (!el) return;
                el.setAttribute("gs-w", w);
                el.setAttribute("gs-h", h);
                el.setAttribute("gs-x", x);
                el.setAttribute("gs-y", y);
                el.setAttribute("gs-no-move", false);
                el.setAttribute("gs-min-w", ItemTypeProperties[type]?.minW);
                el.setAttribute("gs-min-h", ItemTypeProperties[type]?.minH);
                el.setAttribute("gs-max-w", ItemTypeProperties[type]?.maxW);
                el.setAttribute("gs-max-h", ItemTypeProperties[type]?.maxH);
                gridRef.current.makeWidget(el);
              });

              gridRef.current.batchUpdate(false);
            }
          }
        };

    const lastSelectedId = useSelector(
      (state) => state.dashboard.lastClickedItemId
    );

    const sendDashboardSettings = () => {
      const gridOptions = gridRef.current.opts;
      const itemWidth = Math.floor(
        gridRef.current.offsetWidth / gridOptions.column
      );
      const itemHeight = Math.floor(
        gridRef.current.offsetHeight / gridOptions.maxRow
      );

      const dashboardSettings = {
        columns: gridOptions.column,
        rows: gridOptions.row,
        maxRows: gridOptions.maxRow,
        itemWidth: itemWidth,
        itemHeight: itemHeight,
      };
      getDashboardSettings(dashboardSettings);
    };

    useEffect(() => {
      if (!gridRef.current) {
        gridRef.current =
          gridRef.current ||
          GridStack.init(
            {
              float: true,
              handle: ".card-header",
              removable: "#trash",
              acceptWidgets: true,
              // row: 6,
              column: 12,
              // minRow: 6,
              maxRow: 10,
              cellHeight: "auto",

              //resizable: false,
            },
            ".controlled"
          );

        GridStack.setupDragIn(".sidepanel>.grid-stack-item", {
          appendTo: "body",
          helper: "clone",
        });
        //When a widget is dragged onto the dashboard.
        gridRef.current.on("dropped", (event, previousWidget, newWidget) => {
          console.log("New widget dropped in!", newWidget);
          addItem(newWidget);
          gridRef.current.removeWidget(newWidget.el);
        });

      
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
      }
    }, []);

    useEffect(() => {
      const grid = gridRef.current;
      if (!grid) return;

      grid.batchUpdate();
      grid.removeAll(true); // clear grid
      items.forEach(({ id, type }) => {
        const el = refs.current[id]?.current;
        if (!el) return;
        el.setAttribute("gs-min-w", ItemTypeProperties[type]?.minW);
        el.setAttribute("gs-min-h", ItemTypeProperties[type]?.minH);
        el.setAttribute("gs-max-w", ItemTypeProperties[type]?.maxW);
        el.setAttribute("gs-max-h", ItemTypeProperties[type]?.maxH);
        grid.makeWidget(el);
      });

      grid.batchUpdate(false);

      setTimeout(() => {
        handleResize();
      }, 500);
    }, [items]);

    return (
      <div className="">
        <div
          className={`${styles["grid-container"]} mx-auto w-full  max-w-[1300px] relative rounded-xl will-change-transform bg-red-500`}
          id="grid-container"
        >
          <div className="p-1 relative rounded-xl z-0 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-[length:200%] opacity-100 animate-gradient-x shadow-[0_0_80px_rgba(168,85,247,0.6)]">
            <div className="absolute top-0 left-0 w-full h-full ">
              <NetworkEffect containerRef={gridContainerRef} />
            </div>

            <div className={`grid-stack controlled`} ref={gridContainerRef}>
              {items.length > 0 &&
                items.map((item, i) => {
                  if(isPhone === true && item.type === "context"){
                    return <div key={item.id}></div>;
                  }
                  return (
                    <div
                      ref={refs.current[item.id]}
                      key={item.id}
                      gs-x={item.x}
                      gs-y={item.y}
                      gs-w={item.w}
                      gs-h={item.h}
                      gs-id={item.id}
                      itemdata={item.itemdata}
                      className={"grid-stack-item"}
                      onClick={(e) => {
                        if (item.type === "context") {
                          return;
                        }
                        dispatch(setlastClickedItemId(item.id));
                      }}
                      onTouchStart={(e) => {
                        dispatch(setlastClickedItemId(item.id));
                      }}
                    >
                      <div
                        className="grid-stack-item-content"
                        ref={itemContainerRefs.current[item.id]}
                        style={{ overflow: "visible" }}
                      >
                        <Item
                          dashboardMode={currentDashboardMode}
                          item={item}
                          onRemove={
                            removeItem != null
                              ? ({ e, item }) => {
                                  gridRef.current.save();
                                  removeItem(item);
                                }
                              : null
                          }
                          highlighted={lastSelectedId === item.id}
                          containerRef={itemContainerRefs.current[item.id]}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default Dashboard;
